import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, ArrowRight, CheckCircle2, Building2 } from "lucide-react";
import api from "@/lib/api";

const INDUSTRY_COLORS = {
  "Media & Streaming": "from-red-500/20 to-red-500/0 border-red-400/30 text-red-300",
  "E-commerce & Personalization": "from-purple-500/20 to-purple-500/0 border-purple-400/30 text-purple-300",
  "Retail": "from-indigo-500/20 to-indigo-500/0 border-indigo-400/30 text-indigo-300",
  "Healthcare": "from-rose-500/20 to-rose-500/0 border-rose-400/30 text-rose-300",
  "Banking & Financial Services": "from-emerald-500/20 to-emerald-500/0 border-emerald-400/30 text-emerald-300",
  "Telecommunications": "from-amber-500/20 to-amber-500/0 border-amber-400/30 text-amber-300",
  "Service Cloud": "from-amber-500/20 to-amber-500/0 border-amber-400/30 text-amber-300",
  "Platform Development": "from-orange-500/20 to-orange-500/0 border-orange-400/30 text-orange-300",
};

export default function CaseStudiesPage({ apiPrefix = "/case-studies", detailBasePath = "/case-studies", title = "Enterprise-Grade Projects", subtitle = "Solve real business problems from Netflix, Amazon, and more — build the architecture, write the SQL, get AI-reviewed, and add a portfolio-ready project to your public profile.", testId = "case-studies-page" }) {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(apiPrefix).then((res) => setCaseStudies(res.data)).finally(() => setLoading(false));
  }, [apiPrefix]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="case-studies-loading">
        <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid={testId}>
      <div className="flex items-center gap-3 mb-2">
        <Briefcase className="w-6 h-6 text-indigo-400" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">Real Company Case Studies</span>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">{title}</h1>
      <p className="text-sm sm:text-base text-slate-400 mt-2 max-w-2xl">{subtitle}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-8">
        {caseStudies.map((cs, i) => {
          const completed = cs.status === "completed";
          return (
            <motion.div
              key={cs.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.08 }}
              className={`glass-panel rounded-2xl p-6 relative overflow-hidden bg-gradient-to-br ${INDUSTRY_COLORS[cs.industry] || ""}`}
              data-testid={`case-study-card-${cs.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5" />
                </div>
                {completed && (
                  <span className="flex items-center gap-1 text-[11px] font-bold text-emerald-400" data-testid={`case-study-completed-badge-${cs.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> {cs.overall_score}% Score
                  </span>
                )}
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider">{cs.industry} · {cs.difficulty}</span>
              <h2 className="font-heading text-lg font-semibold text-white mt-1">{cs.title}</h2>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{cs.tagline}</p>
              <div className="flex items-center justify-between mt-5">
                <span className="text-xs font-bold text-[#FBBF24]">+{cs.xp_reward} XP</span>
                <Link
                  to={`${detailBasePath}/${cs.id}`} data-testid={`case-study-enter-btn-${cs.id}`}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-primary hover:bg-primary/90 px-4 py-2 rounded-lg transition-colors"
                >
                  {completed ? "Review" : cs.status === "in_progress" ? "Continue" : "Start Case Study"} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
